import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import PageTitle from "components/PageTitle/PageTitle";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";

import useStyles from "./styles";
import { format } from "date-fns";
import logo from "images/logoPagesRS.jpg";

import { cleanEmptyKeys } from "utils/cleanEmptyKeys";
import { dbGet, dbPut } from "utils/DBFetchers";
import { roleTranslation } from "pages/users/roles/utils";

const blankProfileData = {
  id: null,
  username: "",
  email: "",
  name: "",
  lastName: "",
  role: null,
  distributorId: null,
  distributor: null,
  lastActivityAt: null,
  pharmaGroupId: null,
  pharmaGroup: null,
  pharmacyId: null,
  pharmacy: null,
  registeredOn: null,
  updatedAt: null,
  password: "",
  repeatPassword: "",
  isEnabled: null,
};

const getPasswordCharacters = (password) => Array.from(password);
const isLetter = (char) => char.toLowerCase() !== char.toUpperCase();
const isNumber = (char) => /\d/.test(char);
const isUppercaseLetter = (char) =>
  isLetter(char) && char === char.toUpperCase();
const isLowercaseLetter = (char) =>
  isLetter(char) && char === char.toLowerCase();
const isSpecialCharacter = (char) =>
  !isLetter(char) && !isNumber(char) && !/\s/.test(char);

const passwordRequirements = [
  {
    label: "Al menos una mayúscula",
    isValid: (password) =>
      getPasswordCharacters(password).some(isUppercaseLetter),
  },
  {
    label: "Al menos una minúscula",
    isValid: (password) =>
      getPasswordCharacters(password).some(isLowercaseLetter),
  },
  {
    label: "Al menos un carácter especial",
    isValid: (password) =>
      getPasswordCharacters(password).some(isSpecialCharacter),
  },
  {
    label: "Al menos un número",
    isValid: (password) => /\d/.test(password),
  },
  {
    label: "Entre 8 y 20 caracteres",
    isValid: (password) => password.length >= 8 && password.length <= 20,
  },
];

const getPasswordValidation = (password) =>
  passwordRequirements.map((requirement) => ({
    ...requirement,
    valid: requirement.isValid(password),
  }));

const isPasswordStrongEnough = (password) =>
  getPasswordValidation(password).every((requirement) => requirement.valid);

// TODO: Show pharmacy/distrib/group NAME with id provided

export default function AccountProfileDetails(props) {
  var classes = useStyles();
  const [profileData, setProfileData] = useState(blankProfileData);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();
  const passwordValidation = getPasswordValidation(profileData.password);
  const passwordHasValue = profileData.password.length > 0;
  const repeatPasswordHasValue = profileData.repeatPassword.length > 0;
  const passwordChangeHasValue = passwordHasValue || repeatPasswordHasValue;
  const passwordIsValid = isPasswordStrongEnough(profileData.password);
  const passwordsMatch = profileData.password === profileData.repeatPassword;
  const passwordFieldsAreEmpty = !passwordHasValue && !repeatPasswordHasValue;
  const passwordChangeIsValid =
    !passwordOpen ||
    passwordFieldsAreEmpty ||
    (passwordHasValue &&
      passwordIsValid &&
      repeatPasswordHasValue &&
      passwordsMatch);
  const profileFormIsValid =
    Boolean(profileData.username) &&
    Boolean(profileData.email.trim()) &&
    passwordChangeIsValid;
  const fullName = [profileData.name, profileData.lastName]
    .filter(Boolean)
    .join(" ");

  const getPasswordVisibilityButton = () => (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
        onClick={() => setShowPassword(!showPassword)}
        onMouseDown={(event) => event.preventDefault()}
        edge="end"
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  const handleChange = (event) => {
    setProfileData({
      ...profileData,
      [event.target.name]: event.target.value,
    });
  };

  /* Fills form data */
  const fillData = (data) => {
    if (!data) return;
    setProfileData({
      ...profileData,
      id: data.id,
      username: data.username ? data.username : "",
      email: data.email ? data.email : "",
      name: data.name ? data.name : "",
      lastName: data.lastname ? data.lastname : "",
      role: data.role,
      distributorId: data.distributor_id,
      distributor: data.distributor,
      lastActivityAt: data.last_activity_at,
      pharmaGroupId: data.pharma_group_id,
      pharmaGroup: data.pharma_group,
      pharmacyId: data.pharmacy_id,
      pharmacy: data.pharmacy,
      registeredOn: data.registered_on,
      updatedAt: data.updated_at,
      isEnabled: data.is_enabled,
    });
  };

  /* Basic form check */
  const checkFormComplete = () => {
    if (!profileData.username || !profileData.email.trim())
      return false;
    return true;
  };

  /* Submit handling */
  const handleSubmit = () => {
    setIsLoading(true);
    if (!checkFormComplete()) {
      snackbarOpen(
        "error",
        "Hay campos sin completar o incorrectos. Por favor completelos para poder editar el usuario.",
      );
      setIsLoading(false);
      return;
    }
    if (passwordChangeHasValue && !passwordIsValid) {
      snackbarOpen(
        "error",
        "La contraseña debe tener entre 8 y 20 caracteres e incluir mayúscula, minúscula, número y carácter especial.",
      );
      setIsLoading(false);
      return;
    }
    if (passwordChangeHasValue && !passwordsMatch) {
      snackbarOpen("error", "Las contraseñas ingresadas no coinciden.");
      setIsLoading(false);
      return;
    }

    // Give adecuate format to send form to db
    let completedForm = {
      username: profileData.username,
      email: profileData.email,
      name: profileData.name,
      lastname: profileData.lastName,
      password: profileData.password,
    };

    // Cleans empty form fields
    completedForm = cleanEmptyKeys(completedForm);

    // Sends to db
    dbPut(`users/${profileData.id}`, completedForm)
      .then((data) => {
        snackbarOpen("success", "Usuario editado exitosamente");
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      })
      .catch((error) => {
        setIsLoading(false);
        snackbarOpen(
          "error",
          error[0]
            ? error[0].description_es
            : "No se pudo editar el usuario. Intente nuevamente.",
        );
        return;
      });
  };

  /* On page load, get all user data */
  const getProfile = () => {
    dbGet("profile").then((data) => {
      fillData(data);
    });
  };
  React.useEffect(() => {
    getProfile();
  }, []);

  return (
    <>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      <div>
        <PageTitle
          title={`Usuario: ${profileData.username}`}
          // button={
          //   <img src={logo} alt="logo" className={classes.logotypeImage} />
          // }
        />
      </div>
      <form autoComplete="off" noValidate {...props}>
        <Card {...props}>
          <CardContent>
            <Box>
              <Typography color="textPrimary" gutterBottom variant="h2">
                {`${
                  profileData.name || profileData.lastName
                    ? ""
                    : profileData.username
                }${profileData.name ? profileData.name : ""} ${
                  profileData.lastName ? profileData.lastName : ""
                }`}
              </Typography>
              <Typography color="textSecondary" variant="h6">
                {`Email: ${profileData.email}`}
              </Typography>
              <Typography color="textSecondary" variant="h6">
                {`Rol: ${
                  profileData.role
                    ? profileData.role.name in roleTranslation
                      ? roleTranslation[profileData.role.name]
                      : profileData.role.name
                    : ""
                }`}
              </Typography>
              {profileData.pharmacy &&
              Object.keys(profileData.pharmacy).length !== 0 ? (
                <Typography color="textSecondary" variant="h6">
                  {`Farmacia: ${profileData.pharmacy.name} - ${profileData.pharmacy.code}`}
                </Typography>
              ) : null}
              {profileData.distributor &&
              Object.keys(profileData.distributor).length !== 0 ? (
                <Typography color="textSecondary" variant="h6">
                  {`Distribuidor: ${profileData.distributor.name}`}
                </Typography>
              ) : null}
              {profileData.pharmaGroup &&
              Object.keys(profileData.pharmaGroup).length !== 0 ? (
                <Typography color="textSecondary" variant="h6">
                  {`Grupo: ${profileData.pharmaGroup.name}`}
                </Typography>
              ) : null}
              <Typography
                style={{ color: profileData.isEnabled ? "green" : "red" }}
                variant="h6"
              >
                {`Estado: ${profileData.isEnabled ? "Activo" : "Inactivo"}`}
              </Typography>
              <Typography color="textSecondary" variant="h6">
                {`Registrado el ${format(
                  new Date(profileData.registeredOn),
                  "dd-MM-yyyy",
                )}`}
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Card style={{ marginTop: "5%" }}>
          <CardHeader
            title="Datos del usuario"
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  helperText="Usuario"
                  name="username"
                  onChange={handleChange}
                  required
                  value={profileData.username}
                  disabled={true}
                  variant="outlined"
                  className={classes.generalPurposeField}
                />
              </Grid>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  helperText="Nombre y apellido"
                  value={fullName}
                  disabled={true}
                  variant="outlined"
                  className={classes.generalPurposeField}
                />
              </Grid>
            </Grid>
          </CardContent>

          <Divider />
          <CardHeader title="Correo" />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                <TextField
                  fullWidth
                  label="Correo"
                  name="email"
                  onChange={handleChange}
                  required
                  value={profileData.email}
                  variant="outlined"
                  className={classes.generalPurposeField}
                />
              </Grid>
            </Grid>
          </CardContent>

          <Divider />
          <CardHeader
            title="Contraseña"
            subheader={
              passwordOpen
                ? "Si no ingresa contraseña, esta no se modificará."
                : ""
            }
          />
          <CardContent>
            <Grid container spacing={3}>
              <Grid item md={6} xs={12}>
                {passwordOpen ? (
                  <>
                    <Grid>
                      <TextField
                        fullWidth
                        label="Nueva contraseña"
                        name="password"
                        onChange={handleChange}
                        value={profileData.password}
                        variant="outlined"
                        type={showPassword ? "text" : "password"}
                        InputProps={{
                          endAdornment: getPasswordVisibilityButton(),
                        }}
                        error={passwordHasValue && !passwordIsValid}
                      />
                    </Grid>
                    <Box mt={2} />
                    <Grid>
                      <TextField
                        fullWidth
                        label="Confirmar contraseña"
                        name="repeatPassword"
                        onChange={handleChange}
                        value={profileData.repeatPassword}
                        variant="outlined"
                        type={showPassword ? "text" : "password"}
                        InputProps={{
                          endAdornment: getPasswordVisibilityButton(),
                        }}
                        error={repeatPasswordHasValue && !passwordsMatch}
                        helperText={
                          repeatPasswordHasValue && !passwordsMatch
                            ? "Las contraseñas ingresadas no coinciden"
                            : ""
                        }
                      />
                    </Grid>
                    <div className={classes.passwordRequirements}>
                      {passwordValidation.map((requirement) => (
                        <Typography
                          key={requirement.label}
                          className={
                            requirement.valid
                              ? classes.passwordRequirementValid
                              : classes.passwordRequirementInvalid
                          }
                        >
                          {requirement.valid ? "✓" : "✕"} {requirement.label}
                        </Typography>
                      ))}
                    </div>
                  </>
                ) : (
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => setPasswordOpen(!passwordOpen)}
                  >
                    Modificar contraseña
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
          <Divider />
          <Grid container spacing={10}>
            <Grid item xs={12}>
              <div className={classes.buttonSpacing}>
                {isLoading ? (
                  <CircularProgress size={26} className={classes.loginLoader} />
                ) : (
                  <Button
                    color="primary"
                    variant="contained"
                    disabled={!profileFormIsValid}
                    onClick={handleSubmit}
                  >
                    Guardar
                  </Button>
                )}
              </div>
            </Grid>
          </Grid>
        </Card>
      </form>
    </>
  );
}
